import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import styled from 'styled-components';
import { Button } from 'atoms';

const CustomModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 4px;
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
		padding: 0;
		text-align: center;
		padding-inline: 50px;
	}
	.ant-modal-body {
		padding: 0;
	}
`;

const ModalContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 28px;
	padding: 0px 0px 24px;
`;

const UpperContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;
	padding: 20px 0px 0px;
	width: 100%;
`;

const WarningIcon = styled.div`
	svg {
		width: 36px;
		height: 32px;
	}
`;

const TextContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	align-self: stretch;
	gap: 13px;
`;

const TitleContainer = styled.div`
	display: flex;
	width: 470px;
	gap: 24px;
`;

const WarningText = styled.p`
	font-family: Inter, sans-serif;
	font-weight: 600;
	font-size: 16px;
	line-height: 1.25em;
	text-align: center;
	color: #000000;
	margin: 0;
`;

const QuestionContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-self: stretch;
	gap: 8px;
`;

const QuestionText = styled.p`
	font-family: Inter, sans-serif;
	font-weight: 400;
	font-size: 14px;
	line-height: 1.21em;
	color: #858384;
	margin: 0;
`;

const ActionButtons = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 28px;
	width: 100%;
	padding: 0 30px;
`;

const NoButton = styled(Button)`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 10px;
	padding: 8px 16px;
	width: 135.5px;
	height: 40px;
	border-radius: 2px;
	border: 0.5px solid #173c58;
	color: #173c58;
	font-family: Inter, sans-serif;
	font-weight: 500;
	font-size: 16px;
	line-height: 1.21em;
	background: #ffffff;
`;

const YesButton = styled(Button)`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 10px;
	padding: 8px 16px;
	width: 135.5px;
	height: 40px;
	border-radius: 2px;
	background: #143e60;
	color: #ffffff;
	font-family: Inter, sans-serif;
	font-weight: 500;
	font-size: 16px;
	line-height: 1.21em;
	border: none;
`;

/**
 * Warning shown when a trip export has no date range or a range > 365 days.
 * Confirming proceeds with the last-60-days fallback (caller decides the dates).
 * @param {boolean} open
 * @param {() => void} onConfirm  proceed with last-60-days export
 * @param {() => void} onCancel   dismiss without exporting
 */
function TripExportWarningModal({ open, onConfirm, onCancel }) {
	return (
		<CustomModal
			open={open}
			onCancel={onCancel}
			footer={null}
			closable={false}
			style={{ top: 200 }}
			width={488}
		>
			<ModalContainer>
				<UpperContent>
					<WarningIcon>
						<svg
							width="36"
							height="32"
							viewBox="0 0 36 32"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M1.76885 31.75C1.44802 31.75 1.15635 31.6701 0.893854 31.5103C0.631354 31.3504 0.427188 31.1387 0.281354 30.875C0.135521 30.6113 0.0556043 30.3267 0.0416043 30.021C0.0276043 29.7153 0.107521 29.4167 0.281354 29.125L16.4689 1.125C16.6439 0.833333 16.8702 0.614583 17.1479 0.46875C17.4255 0.322917 17.7096 0.25 18.0001 0.25C18.2906 0.25 18.5753 0.322917 18.8541 0.46875C19.1329 0.614583 19.3587 0.833333 19.5314 1.125L35.7189 29.125C35.8939 29.4167 35.9744 29.7159 35.9604 30.0228C35.9464 30.3296 35.8659 30.6137 35.7189 30.875C35.5719 31.1363 35.3677 31.3481 35.1064 31.5103C34.845 31.6724 34.5534 31.7523 34.2314 31.75H1.76885ZM4.7876 28.25H31.2126L18.0001 5.5L4.7876 28.25ZM18.0001 26.5C18.4959 26.5 18.9119 26.332 19.2479 25.996C19.5839 25.66 19.7513 25.2447 19.7501 24.75C19.7489 24.2553 19.5809 23.84 19.2461 23.504C18.9113 23.168 18.4959 23 18.0001 23C17.5043 23 17.0889 23.168 16.7541 23.504C16.4193 23.84 16.2513 24.2553 16.2501 24.75C16.2489 25.2447 16.4169 25.6606 16.7541 25.9977C17.0913 26.3349 17.5066 26.5023 18.0001 26.5ZM18.0001 21.25C18.4959 21.25 18.9119 21.082 19.2479 20.746C19.5839 20.41 19.7513 19.9947 19.7501 19.5V14.25C19.7501 13.7542 19.5821 13.3388 19.2461 13.004C18.9101 12.6692 18.4948 12.5012 18.0001 12.5C17.5054 12.4988 17.0901 12.6668 16.7541 13.004C16.4181 13.3412 16.2501 13.7565 16.2501 14.25V19.5C16.2501 19.9958 16.4181 20.4118 16.7541 20.7478C17.0901 21.0838 17.5054 21.2512 18.0001 21.25Z"
								fill="#FF3B30"
							/>
						</svg>
					</WarningIcon>
					<TextContainer>
						<TitleContainer>
							<WarningText>
								You are trying to export a large volume of data. Limit your
								selection to a shorter date range
							</WarningText>
						</TitleContainer>
						<QuestionContainer>
							<QuestionText>
								Do you want to proceed with last 60 days data
							</QuestionText>
						</QuestionContainer>
					</TextContainer>
				</UpperContent>
				<ActionButtons>
					<NoButton onClick={onCancel}>No</NoButton>
					<YesButton type="primary" onClick={onConfirm}>
						Yes
					</YesButton>
				</ActionButtons>
			</ModalContainer>
		</CustomModal>
	);
}

TripExportWarningModal.propTypes = {
	open: PropTypes.bool,
	onConfirm: PropTypes.func,
	onCancel: PropTypes.func,
};

export default TripExportWarningModal;
